import {DOCUMENT, inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {STATIC_CONFIGS} from '../../../configs/static.config';

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    private readonly document = inject(DOCUMENT);
    private readonly translateService = inject(TranslateService);
    private currentIntlLocale: Intl.Locale | undefined;

    public getCurrentIntlLocale(): Intl.Locale | undefined {
        return this.currentIntlLocale;
    }

    public setCurrentIntlLocale(newLocale: Intl.Locale): void {
        let currentIntlLocale: Intl.Locale | undefined;

        if (this.getIsSupportedLanguageByLocale(newLocale)) {
            if (this.getIsSupportedRegionByLocale(newLocale)) {
                currentIntlLocale = newLocale;
            } else {
                currentIntlLocale = this.getSupportedLocaleWithDefaultRegionByLanguage(newLocale.language);
            }
        }

        currentIntlLocale ??= this.getDefaultLocale();

        if (currentIntlLocale.language === this.translateService.getCurrentLang()) {
            return;
        }

        this.translateService.use(currentIntlLocale.language);
        this.setDocumentLanguage(currentIntlLocale.language);

        this.currentIntlLocale = currentIntlLocale;
    }

    public getCurrentBrowserIntlLocale(): Intl.Locale {
        return new Intl.Locale(navigator.language);
    }

    public setBrowserLocaleAsCurrentIntlLocale(): void {
        this.setCurrentIntlLocale(this.getCurrentBrowserIntlLocale());
    }

    public getIsSupportedLanguageByLocale(locale: Intl.Locale): boolean {
        return STATIC_CONFIGS.language.supportedLocaleNames.some(supportedLocaleName => {
            return new Intl.Locale(supportedLocaleName).language === locale.language;
        });
    }

    public getIsSupportedRegionByLocale(locale: Intl.Locale): boolean {
        return STATIC_CONFIGS.language.supportedLocaleNames.some(supportedLocaleName => {
            const supportedLocale = new Intl.Locale(supportedLocaleName);

            return (supportedLocale.language === locale.language) && (supportedLocale.region === locale.region);
        });
    }

    public getDefaultLocale(): Intl.Locale {
        return new Intl.Locale(STATIC_CONFIGS.language.defaultLocaleName);
    }

    public getSupportedLocaleWithDefaultRegionByLanguage(localeLanguage: string): Intl.Locale | undefined {
        const defaultRegionLocaleName = STATIC_CONFIGS.language.supportedLocaleNames.find(supportedLocaleName => {
            return new Intl.Locale(supportedLocaleName).language === localeLanguage;
        });

        return defaultRegionLocaleName ? new Intl.Locale(defaultRegionLocaleName) : undefined;
    }

    private setDocumentLanguage(lang: string): void {
        this.document.documentElement.setAttribute('lang', lang);
    }
}
